import React, { useEffect, useState } from 'react';
import { rbacApi } from '../api/rbac.api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Plus, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import useInLeadershipShell from '@/hooks/useInLeadershipShell';
import LeadershipPageHeader from '@/components/LeadershipPageHeader';
import { LEADERSHIP_CONTENT } from '@/config/leadershipLayout';

export default function SpecialisationsPage() {
  const lead = useInLeadershipShell();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await rbacApi.loadSpecialisations());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load specialisations');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await rbacApi.createSpecialisation({ name: name.trim(), description });
      toast.success('Specialisation added');
      setName('');
      setDescription('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    }
    setSaving(false);
  };

  const toggle = async (row) => {
    try {
      await rbacApi.updateSpecialisation(row.id, { is_active: row.is_active === false });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className={`${lead ? LEADERSHIP_CONTENT : 'p-6 max-w-4xl mx-auto'} space-y-6`} data-testid="specialisations-page">
      {lead ? (
        <LeadershipPageHeader
          title="Specialisations"
          subtitle="Clinical specialisations used when adding doctors and consultants."
        />
      ) : (
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 font-heading">Specialisation master</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Maintain clinical specialisations used when adding doctors and consultants.
          </p>
        </div>
      )}

      <div className="bg-white border rounded-xl p-5 space-y-3">
        <p className="text-sm font-semibold text-zinc-800 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add specialisation
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Cardiology" data-testid="spec-name" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
        <Button onClick={create} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="spec-create">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save
        </Button>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-zinc-400" /></div>
        ) : (
          <ul className="divide-y">
            {rows.map((r) => (
              <li key={r.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`spec-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-zinc-900 flex items-center gap-1.5">
                    <Stethoscope className="h-3.5 w-3.5 text-orange-600" /> {r.name}
                  </p>
                  {r.description && <p className="text-[12px] text-zinc-500 mt-0.5">{r.description}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.is_active === false ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {r.is_active === false ? 'Inactive' : 'Active'}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => toggle(r)}>
                    {r.is_active === false ? 'Activate' : 'Deactivate'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
